<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useForm } from 'vee-validate';
import { yup } from '@clearbanc/validation';
import { BusinessDetails } from '@/types/business.types';
import { useDocumentUploads } from '@/composables/useDocumentUploads';
import { useStore } from 'vuex';
import { BUSINESS_DOC_TYPE, ENTITY_TYPE } from '@clearbanc/data-common-types';

import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import CalendarGroup from '@/components/forms/groups/CalendarGroup.vue';
import UploadFileWithDownloadableFileList from '@/components/upload/UploadFileWithDownloadableFileList.vue';

interface BusinessIncorporationForm {
  corpDate?: Date | string;
  articlesOfIncorporation?: string;
}

const store = useStore();
const props = defineProps<{ business: BusinessDetails }>();

const { areDocumentUploadsRequired, areDocumentUploadsHidden } =
  useDocumentUploads();

const incorporationDocuments = computed(() =>
  store.getters.uploadsMatching(store.getters.incorporationUploadMatcher),
);

const validationSchema = yup.object({
  corpDate: yup.incorporationDate(),
  articlesOfIncorporation: (yup.string().nullable() as any).requiredWhen(
    [],
    () => {
      if (!areDocumentUploadsRequired.value) {
        return false;
      }

      return incorporationDocuments.value.length < 1;
    },
    'Please upload your business incorporation documents.',
  ),
});

function removeUpload(uploadId: number) {
  store.dispatchApiAction('DELETE_USER_UPLOAD', { id: uploadId });
}

const { values, setValues, validate, validateField } =
  useForm<BusinessIncorporationForm>({
    validationSchema,
  });

async function validateAndGetValues() {
  const { valid } = await validate();
  if (valid) {
    return {
      corpDate: values.corpDate,
    };
  }
  return undefined;
}

defineExpose({
  validateAndGetValues,
});

function populateSavedValues() {
  const { corpDate } = props.business;

  setValues({
    ...(corpDate && { corpDate: corpDate.toString().split('T')[0] }),
  });
}

onMounted(() => {
  populateSavedValues();
});

watch(incorporationDocuments, () => {
  validateField('articlesOfIncorporation');
});
</script>

<template>
  <div class="c-group">
    <div class="c-group__header">
      <h3 class="c-group__header__title">Business Incorporation Documents</h3>
    </div>
    <div class="c-form__row grid grid-cols-12">
      <div class="col-span-6 md:col-span-12">
        <CalendarGroup
          label="Business Incorporation Date"
          name="corpDate"
          :manual-input="false"
          :max-date="new Date()"
          placeholder="yyyy-mm-dd"
          show-icon
        />
      </div>
    </div>
    <div class="c-form__row grid grid-cols-12" v-if="!areDocumentUploadsHidden">
      <div class="col-span-6 md:col-span-12">
        <h3 :class="$style['field-heading']">
          Upload a copy of the Articles of Incorporation
        </h3>
        <p :class="$style['field-description']">
          Please provide the official document issued when your company was
          formed (e.g., Articles of Incorporation)
        </p>
        <UploadFileWithDownloadableFileList
          ref="upload-file-with-downloadable-file-list"
          :upload-filters="{
            entity: ENTITY_TYPE.BUSINESS,
            type: BUSINESS_DOC_TYPE.CORP_DOCS,
            businessId: store.getters.business.id,
          }"
          :display-filters="store.getters.incorporationUploadMatcher"
          empty-prompt="Drag & drop files here or browse"
          theme="onboarding"
          no-list
          :files="incorporationDocuments"
          :is-closable="incorporationDocuments.length > 1"
          @fileRemove="removeUpload"
        />
        <InputTextGroup
          name="articlesOfIncorporation"
          label=""
          :style="{ display: 'none' }"
        />
      </div>
      <div class="col-span-6 md:col-span-12">
        <h3 :class="$style['field-heading']">
          Upload a copy of the EIN document
        </h3>
        <p :class="$style['field-description']">
          Please upload the official EIN verification letter your business
          received from the IRS after applying for an EIN. You can do this now
          or anytime before funding. Learn more
        </p>
        <UploadFileWithDownloadableFileList
          ref="upload-file-with-downloadable-file-list"
          :upload-filters="{
            entity: ENTITY_TYPE.BUSINESS,
            type: BUSINESS_DOC_TYPE.EIN_DOC,
            businessId: store.getters.business.id,
          }"
          empty-prompt="Drag & drop files here or browse"
          theme="onboarding"
          no-list
          :files="store.getters.einDocuments"
          @fileRemove="removeUpload"
        />
      </div>
    </div>
  </div>
</template>

<style module scoped lang="less">
.field-heading {
  color: var(--neutral-grey-400, #5f5f61);
  font-family: @regular-font;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  margin: 0 0 4px 0;
}

.field-description {
  color: var(--neutral-grey-500, #303032);
  font-family: @regular-font;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 20px;
  margin: 0 0 20px 0;
}
</style>
